import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Calendar, User, Share2, MessageCircle, Award, Copy, Check, X } from 'lucide-react';
import { blogService } from '../../services/blogService';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    loadPost();
    loadComments();
  }, [slug]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareRef?.current && !shareRef?.current?.contains(e?.target)) {
        setShareOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPost = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getPostBySlug(slug);
      setPost(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const data = await blogService?.getCommentsByPostSlug(slug);
      setComments(data || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  const handleSubmitComment = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      alert('Yorum yapmak için giriş yapmalısınız');
      navigate('/login');
      return;
    }

    if (!commentText?.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      await blogService?.createComment(post?.id, commentText);
      setCommentText('');
      await loadComments();
    } catch (err) {
      alert('Yorum gönderilemedi: ' + err?.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = async () => {
    const url = window.location?.href;
    const title = post?.title || 'Blog yazısı';

    // Mobile: use native Web Share API
    if (navigator.share && /Mobi|Android|iPhone|iPad/i?.test(navigator.userAgent)) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
          handleCopyLink();
        }
      }
      return;
    }

    // Desktop: toggle dropdown
    setShareOpen((prev) => !prev);
  };

  const handleCopyLink = async () => {
    const url = window.location?.href;
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
    setShareOpen(false);
  };

  const shareToWhatsApp = () => {
    const url = encodeURIComponent(window.location?.href);
    const text = encodeURIComponent((post?.title || '') + ' ');
    window.open(`https://wa.me/?text=${text}${url}`, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(window.location?.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(window.location?.href);
    const text = encodeURIComponent(post?.title || '');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  const shareToInstagram = async () => {
    const url = window.location?.href;
    const title = post?.title || 'Blog yazısı';

    // Mobile: use native share sheet (Instagram Stories appears as an option)
    if (navigator.share && /Mobi|Android|iPhone|iPad/i?.test(navigator.userAgent)) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
          // fallback: copy link
          try {
            await navigator.clipboard?.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {}
        }
      }
      setShareOpen(false);
      return;
    }

    // Desktop: copy link and open Instagram
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    setShareOpen(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The post you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/blog-listing')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Blog
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <button
              onClick={() => navigate('/blog-listing')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Tüm yazılara dön
            </button>
          </div>
        </div>
        {/* Article */}
        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Cover Image */}
          {post?.imageUrl && (
            <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
              <img
                src={post?.imageUrl}
                alt={post?.title}
                className="w-full aspect-video object-cover"
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            {post?.title}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center justify-between mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-6 text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>
                  {new Date(post?.publishedAt || post?.createdAt)?.toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>

            {/* Share Button + Dropdown */}
            <div className="relative" ref={shareRef}>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span>Paylaş</span>
              </button>

              {/* Desktop share dropdown */}
              {shareOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="text-sm font-semibold text-gray-700">Paylaş</span>
                    <button onClick={() => setShareOpen(false)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* WhatsApp */}
                  <button
                    onClick={shareToWhatsApp}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left"
                  >
                    <span className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                  </button>

                  {/* Facebook */}
                  <button
                    onClick={shareToFacebook}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                  >
                    <span className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-700">Facebook</span>
                  </button>

                  {/* Twitter / X */}
                  <button
                    onClick={shareToTwitter}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <span className="w-8 h-8 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-700">Twitter / X</span>
                  </button>

                  {/* Instagram */}
                  <button
                    onClick={shareToInstagram}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-pink-50 transition-colors text-left"
                  >
                    <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)'}}>
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </span>
                    <span className="text-sm font-medium text-gray-700">Instagram</span>
                  </button>

                  {/* Copy Link */}
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
                  >
                    <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                    </span>
                    <span className="text-sm font-medium text-gray-700">{copied ? 'Kopyalandı!' : 'Linki Kopyala'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:list-disc prose-ul:ml-6 prose-li:text-gray-700 prose-strong:text-gray-900"
            dangerouslySetInnerHTML={{ __html: post?.content }}
          />

          {/* Comments Section */}
          <div className="mt-16 pt-8 border-t-2 border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-blue-600" />
              <h2 className="text-3xl font-bold text-gray-900">Yorumlar</h2>
              <span className="text-gray-500 text-lg">({comments?.length})</span>
            </div>

            {/* Badge Incentive Message */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6 mb-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Award className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span>🏆</span>
                    <span>Muhabbet Kuşu Rozeti Kazan!</span>
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    İlk yorumunu yaparak <span className="font-semibold text-yellow-700">"Muhabbet Kuşu"</span> rozetini kazanabilirsin! 
                    Topluluğa katıl, fikirlerini paylaş ve özel rozetlerle profilini öne çıkar. 💬✨
                  </p>
                </div>
              </div>
            </div>

            {/* Comment Form */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Yorum Yaz</h3>
              {user ? (
                <form onSubmit={handleSubmitComment}>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e?.target?.value)}
                    placeholder="Düşüncelerini paylaş..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows="4"
                    disabled={submittingComment}
                  />
                  <div className="flex justify-end mt-4">
                    <button
                      type="submit"
                      disabled={submittingComment || !commentText?.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                    >
                      {submittingComment ? 'Gönderiliyor...' : 'Yorum Yap'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">Yorum yapmak için giriş yapmalısınız</p>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Giriş Yap
                  </button>
                </div>
              )}
            </div>

            {/* Comments List */}
            <div className="space-y-6">
              {comments?.length > 0 ? (
                comments?.map((comment) => (
                  <div key={comment?.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {comment?.author?.avatarUrl ? (
                          <img
                            src={comment?.author?.avatarUrl}
                            alt={comment?.author?.fullName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="w-6 h-6 text-blue-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-gray-900">{comment?.author?.fullName}</span>
                          <span className="text-sm text-gray-500">
                            {new Date(comment?.createdAt)?.toLocaleDateString('tr-TR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{comment?.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
                  <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-lg">Henüz yorum yapılmamış</p>
                  <p className="text-gray-400 text-sm mt-1">İlk yorumu yapan sen ol!</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/blog-listing')}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Tüm yazılara dön
            </button>
          </div>
        </article>
      </div>
    </>
  );
}